import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../../../shared/components/ui/badge/badge.component';
import { AuthService } from '../../../../auth-pages/services/auth.service';
import { MeetingAvailabilityOverrideApiService } from '../../../services/meeting-availability-override-api.service';
import { MeetingAvailabilityOverrideDrawerService } from '../../../services/meeting-availability-override-drawer.service';
import { MeetingUserAvailabilityOverrideResponse } from '../../../models/meeting.model';
import { ConfirmDialogService } from '../../../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../../../shared/services/toast.service';
import { HasPermissionDirective } from '../../../../../shared/directives/has-permission.directive';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-availability-overrides-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
  ],
  templateUrl: './availability-overrides-list.component.html',
})
export class AvailabilityOverridesListComponent implements OnInit, OnDestroy {
  items: MeetingUserAvailabilityOverrideResponse[] = [];
  loading = false;
  error = '';

  targetUserId: number | null = null;

  filtroAvailable: '' | 'true' | 'false' = '';
  filtroDateFrom = '';
  filtroDateTo = '';

  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;
  pageSize = 10;
  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private api: MeetingAvailabilityOverrideApiService,
    private drawerService: MeetingAvailabilityOverrideDrawerService,
    private authService: AuthService,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    const savedPageSize = localStorage.getItem('availability-overrides:pageSize');
    if (savedPageSize) this.pageSize = +savedPageSize;

    const u = this.authService.getCurrentUser();
    this.targetUserId = u?.id || null;

    if (this.targetUserId) this.cargar();

    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  timeString(v: any): string {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 5);
    const h = String(v.hour ?? 0).padStart(2, '0');
    const m = String(v.minute ?? 0).padStart(2, '0');
    return `${h}:${m}`;
  }

  cargar(): void {
    if (!this.targetUserId) return;
    this.loading = true;
    this.error = '';
    this.api.list(this.targetUserId, this.businessId, this.globalPage, this.pageSize).subscribe({
      next: (page) => {
        let data = [...page.content];
        if (this.filtroAvailable !== '') {
          const want = this.filtroAvailable === 'true';
          data = data.filter((i) => !!i.isAvailable === want);
        }
        if (this.filtroDateFrom) {
          data = data.filter((i) => (i.overrideDate || '') >= this.filtroDateFrom);
        }
        if (this.filtroDateTo) {
          data = data.filter((i) => (i.overrideDate || '') <= this.filtroDateTo);
        }
        this.items = data.sort((a, b) =>
          (a.overrideDate || '').localeCompare(b.overrideDate || '')
        );
        this.globalTotalPages = page.totalPages;
        this.globalTotalElements = page.totalElements;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error cargando excepciones';
        this.loading = false;
      },
    });
  }

  buscar(): void {
    this.globalPage = 0;
    this.cargar();
  }

  onPageSizeChange(): void {
    localStorage.setItem('availability-overrides:pageSize', this.pageSize.toString());
    this.globalPage = 0;
    this.cargar();
  }

  goToPage(p: number): void {
    if (p < 0 || p >= this.globalTotalPages) return;
    this.globalPage = p;
    this.cargar();
  }

  nuevo(): void {
    this.drawerService.open({ idUser: this.targetUserId || undefined });
  }

  editar(item: MeetingUserAvailabilityOverrideResponse): void {
    this.drawerService.open({ overrideId: item.id, idUser: item.idUser });
  }

  async eliminar(item: MeetingUserAvailabilityOverrideResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar excepción',
      message: `¿Eliminar la excepción del ${this.fechaLegible(item.overrideDate)}?`
    });
    if (!ok) return;
    this.api.delete(item.id, this.businessId).subscribe({
      next: () => {
        this.cargar();
        this.toast.deleted(
          'Excepción de disponibilidad',
          `Se eliminó la excepción del ${this.fechaLegible(item.overrideDate)} correctamente.`
        );
      },
      error: (err) => { this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar' }); },
    });
  }

  /**
   * Fecha en español ("19 de agosto de 2026") a partir del `LocalDate` del backend.
   * Se parte la cadena a mano: `new Date('2026-08-19')` se interpreta como UTC y
   * en husos negativos mostraría el día anterior.
   */
  fechaLegible(fecha: string | null | undefined): string {
    if (!fecha) return 'sin fecha';
    const [y, m, d] = fecha.split('-').map(Number);
    if (!y || !m || !d) return fecha;
    return new Date(y, m - 1, d).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  trackById = (_: number, item: MeetingUserAvailabilityOverrideResponse) => item.id;
}
